import * as React from "react";
import {
    ActionButton,
    Button,
    Card,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import { Link, useParams } from "react-router-dom";
import { SupportedUploadMethod } from "../../../../../utils/types/filepicker";
import {
    addPracticeParamToUrl,
    formatDate,
} from "../../../../../utils/helpers";
import DocumentViewer from "../../../../../components/DocumentViewer/DocumentViewer";
import FilePicker from "../../../../../components/FilePicker/FilePicker";
import ListState from "../../../../../components/ListState/ListState";
import {
    ClientDocumentType,
    ClientDocumentTypes,
    IClientDocument,
} from "../../../types/clients.types";
import { useFetchDocuments } from "../../../hooks/clients.queries";
import SendIcon from "../../../../../components/Icons/Send";
import Loader from "../../../../../shared-ui/Loader/Loader";
import ChevronRightIcon from "../../../../../icons/ChevronRight";
import { useAppSelector } from "../../../../../hooks/useRedux";

type ModalTypes = "view-document" | "add-document";

export default function InsuranceCardImages() {
    const [modalInView, setModalInView] = React.useState<ModalTypes | null>(
        null
    );
    const [documentInViewDetails, setDocumentInViewDetails] =
        React.useState<IClientDocument | null>();

    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    const { practice } = useAppSelector((state) => state.userPractice);

    // Fetch client documents
    const { data, isLoading, isSuccess, error } = useFetchDocuments({
        client_id: clientId,
        type: ClientDocumentType.INSURANCE_SCAN,
    });

    const handleSetModalInView = (
        modal: ModalTypes | null,
        document?: IClientDocument | null
    ) => {
        setModalInView(() => modal);
        if (document) {
            setDocumentInViewDetails(() => document);
        }
    };

    return (
        <>
            <Dialog open={modalInView === "add-document"}>
                <DialogContent
                    showFooter={false}
                    title="Add insurance card document"
                    variant="center"
                    handleCloseDialog={() => handleSetModalInView(null)}
                >
                    <FilePicker
                        name="filepicker"
                        uploadMethods={[
                            {
                                method: SupportedUploadMethod.manualupload,
                            },
                            {
                                method: SupportedUploadMethod.googledrive,
                            },
                            {
                                method: SupportedUploadMethod.dropbox,
                            },
                        ]}
                        uploadType="insurance-card"
                        successCallback={() => handleSetModalInView(null)}
                        documentToUploadType={
                            ClientDocumentTypes.INSURANCE_SCAN
                        }
                    />
                </DialogContent>
            </Dialog>
            {documentInViewDetails && (
                <Dialog
                    open={
                        modalInView === "view-document" &&
                        Boolean(documentInViewDetails)
                    }
                >
                    <DialogContent
                        showFooter={false}
                        handleCloseDialog={() => handleSetModalInView(null)}
                        wrapperClass="w-[900px]"
                    >
                        <DocumentViewer
                            document={documentInViewDetails}
                            file={documentInViewDetails.url}
                        />
                    </DialogContent>
                </Dialog>
            )}
            {isLoading && <Loader />}
            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display client insurance card documents at this time please try again later`
                    }
                />
            )}
            {data && isSuccess && data.data.length === 0 && (
                <ListState
                    listHeader="Insurance card documents"
                    stateHelperText="Client insurance card documents will appear here once added"
                    isError={false}
                    emptyMessage="Please add front and back view of the document"
                    emptyBtnProps={{
                        showButton: true,
                        buttonText: "Add documents",
                        onButtonClick: () =>
                            handleSetModalInView("add-document"),
                    }}
                />
            )}
            {data && isSuccess && data.data.length > 0 && (
                <Card classNames="p-0">
                    <div className="flex flex-wrap items-center gap-x-[--spacing-items] justify-between px-[--padding-card-x-lg] py-[--padding-card-y-lg]  [border-bottom:1px_solid_var(--color-stroke)]">
                        <h3 className="text-base font-bold">
                            Insurance card documents
                        </h3>
                        <Button
                            type="button"
                            onClick={() => handleSetModalInView("add-document")}
                        >
                            Add documents
                        </Button>
                    </div>
                    <div className="px-[--padding-card-x-lg] py-[--padding-card-y-lg] grid grid-cols-[repeat(auto-fit,minmax(262px,1fr))] gap-2">
                        {data.data.map((doc) => (
                            <Card
                                classNames="flex flex-col gap-y-[--spacing-items]"
                                key={doc.document_id}
                            >
                                <span className="flex items-center gap-x-2 text-caption font-medium text-left">
                                    {doc.title}
                                    <Link
                                        to={addPracticeParamToUrl(
                                            `/client/insurance/${clientId}/${doc.document_id}`,
                                            practice?.practice_id
                                        )}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-textLink"
                                    >
                                        <SendIcon />
                                    </Link>
                                </span>
                                <div className="flex justify-between items-center font-normal text-caption">
                                    {formatDate(doc.uploaded_date)}
                                    <ActionButton
                                        variant="normal"
                                        size="auto"
                                        onClick={() =>
                                            handleSetModalInView(
                                                "view-document",
                                                doc
                                            )
                                        }
                                        actionTitle="View document"
                                        className="text-textLink"
                                        iconRight={<ChevronRightIcon />}
                                    >
                                        View doc
                                    </ActionButton>
                                </div>
                            </Card>
                        ))}
                    </div>
                </Card>
            )}
        </>
    );
}
